import { useState, useEffect } from 'react'
import './TypeWriter.css'

interface TypeWriterProps {
  texts: string[]
  staticTextBefore?: string
  staticTextAfter?: string
  typingSpeed?: number
  pauseDuration?: number
  className?: string
  onTextsChange?: (texts: string[]) => void
}

function TypeWriter({ 
  texts, 
  staticTextBefore = '',
  staticTextAfter = '',
  typingSpeed = 100, 
  pauseDuration = 2000, 
  className = '',
  onTextsChange
}: TypeWriterProps) {
  const [currentTextIndex, setCurrentTextIndex] = useState(0)
  const [currentText, setCurrentText] = useState('')
  const [isTyping, setIsTyping] = useState(true)
  const [charIndex, setCharIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  
  // Load saved texts from localStorage or use default texts
  const [textsArray, setTextsArray] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTexts = localStorage.getItem('typewriter-texts')
      if (savedTexts) {
        try {
          return JSON.parse(savedTexts)
        } catch (error) {
          console.warn('Failed to parse saved texts:', error)
        }
      }
    }
    return texts
  })

  // Notify parent of initial loaded texts
  useEffect(() => {
    if (onTextsChange && typeof window !== 'undefined') {
      const savedTexts = localStorage.getItem('typewriter-texts')
      if (savedTexts) {
        try {
          const parsed = JSON.parse(savedTexts)
          onTextsChange(parsed)
        } catch (error) {
          console.warn('Failed to parse saved texts:', error)
        }
      }
    }
  }, [onTextsChange])

  useEffect(() => {
    const currentFullText = textsArray[currentTextIndex]

    if (isTyping) {
      if (charIndex < currentFullText.length) {
        setIsPaused(false)
        const timeout = setTimeout(() => {
          setCurrentText(currentFullText.slice(0, charIndex + 1))
          setCharIndex(charIndex + 1)
        }, typingSpeed)
        return () => clearTimeout(timeout)
      } else {
        // Finished typing current text, pause before erasing
        setIsPaused(true)
        const timeout = setTimeout(() => {
          setIsTyping(false)
          setIsPaused(false)
        }, pauseDuration)
        return () => clearTimeout(timeout)
      }
    } else {
      if (charIndex > 0) {
        const timeout = setTimeout(() => {
          setCurrentText(currentFullText.slice(0, charIndex - 1))
          setCharIndex(charIndex - 1)
        }, typingSpeed / 2) // Erase faster than typing
        return () => clearTimeout(timeout)
      } else {
        // Finished erasing, move to next text
        const timeout = setTimeout(() => {
          setCurrentTextIndex((prevIndex) => (prevIndex + 1) % textsArray.length)
          setIsTyping(true)
        }, 500) // Short pause before starting next text
        return () => clearTimeout(timeout)
      }
    }
  }, [currentTextIndex, charIndex, isTyping, textsArray, typingSpeed, pauseDuration])

  // Update textsArray when new texts are provided externally
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedTexts = localStorage.getItem('typewriter-texts')
      if (savedTexts) {
        try {
          const parsed = JSON.parse(savedTexts)
          setTextsArray(parsed)
          // Reset animation when texts change
          setCurrentTextIndex(0)
          setCharIndex(0)
          setCurrentText('')
          setIsTyping(true)
        } catch (error) {
          console.warn('Failed to parse saved texts:', error)
        }
      }
    }
  }, [texts])

  return (
    <span className={`typewriter ${className}`}>
      {staticTextBefore && (
        <span 
          className="typewriter-static" 
          dangerouslySetInnerHTML={{ __html: staticTextBefore + ' ' }}
        />
      )}
      <span>
        {currentText}
      </span>
      <span className={`typewriter-cursor ${isPaused ? 'paused' : isTyping ? 'typing' : 'erasing'}`}>|</span>
      {staticTextAfter && (
        <span 
          className="typewriter-static" 
          dangerouslySetInnerHTML={{ __html: ' ' + staticTextAfter }}
        />
      )}
    </span>
  )
}

export default TypeWriter
